import { coerceDate, formatDateLabel } from '../dateLabels';
import type { QuarterlyReportSummary } from '../../types/quarterlyReport';
import type { SurveillancePlanRecord } from '../../types/surveillance';

/**
 * "Since your last review" needs a date the member would recognise as a review.
 *
 * There is no explicit "last review" field anywhere in the schema, but there are
 * three member-readable timestamps that mean it, in descending order of how
 * clearly they read as a review to the member:
 *
 *   1. the newest RELEASED quarterly report — the thing they were written to
 *   2. the released surveillance plan's release/sign-off date
 *   3. their earliest observation — not a review at all, but a defensible
 *      "since you joined" anchor so a member with data but no review yet still
 *      gets a comparison
 *
 * Returning `source: 'none'` is a real answer, not a failure: a member with one
 * set of results and no review has nothing to compare against, and the
 * "what's changed" block hides rather than inventing a baseline.
 */
export type ReviewAnchorSource = 'quarterly_report' | 'surveillance_plan' | 'first_data' | 'none';

export interface ReviewAnchor {
  at: Date | null;
  /** Member-facing phrasing, e.g. "since your report on 12 May 2026". */
  label: string;
  source: ReviewAnchorSource;
}

export interface ReviewAnchorInput {
  releasedReports?: Pick<QuarterlyReportSummary, 'releasedAt'>[];
  latestPlan?: Pick<SurveillancePlanRecord, 'releasedAt' | 'signedOffAt'> | null;
  earliestObservationAt?: Date | null;
}

const NO_ANCHOR: ReviewAnchor = { at: null, label: 'No previous review yet', source: 'none' };

export function resolveLastReviewAnchor(input: ReviewAnchorInput): ReviewAnchor {
  const reportDates = (input.releasedReports || [])
    .map((report) => coerceDate(report.releasedAt))
    .filter((date): date is Date => date !== null)
    .sort((left, right) => right.getTime() - left.getTime());

  if (reportDates[0]) {
    return {
      at: reportDates[0],
      label: `since your report on ${formatDateLabel(reportDates[0])}`,
      source: 'quarterly_report',
    };
  }

  const planDate = coerceDate(input.latestPlan?.releasedAt) || coerceDate(input.latestPlan?.signedOffAt);
  if (planDate) {
    return {
      at: planDate,
      label: `since your plan on ${formatDateLabel(planDate)}`,
      source: 'surveillance_plan',
    };
  }

  const firstData = coerceDate(input.earliestObservationAt);
  if (firstData) {
    return { at: firstData, label: 'since you joined', source: 'first_data' };
  }

  return NO_ANCHOR;
}
